import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useMerchantOrders } from "../hooks/useMerchantOrders";
import { useMerchantRealtime } from "../hooks/useMerchantRealtime";
import { useHeartbeat } from "../hooks/useHeartbeat";
import { useAlarm } from "../alarm/useAlarm";
import { getOrCreateTerminalId } from "../lib/terminalId";
import { groupOrders } from "../lib/orderGrouping";
import { acceptOrder, markPreparing, markReady, rejectOrder } from "../lib/orderActions";
import { humanizeError } from "../lib/errors";
import { OrderCard } from "../components/OrderCard";
import { AlarmBanner } from "../components/AlarmBanner";
import { TerminalStatusBar } from "../components/TerminalStatusBar";
import { SetupPanel } from "../components/SetupPanel";

// Zentrale operative Terminal-Ansicht. Orchestriert Initial Load,
// Realtime-Signale, Heartbeat und Alarm. Source of Truth ist immer der
// neu geladene Serverzustand.
export function MerchantTerminal() {
  const { profile, merchantId, merchantName, signOut } = useAuth();
  const terminalId = useRef<string>(getOrCreateTerminalId());
  const { orders, loadedOnce, error, reload } = useMerchantOrders(merchantId);
  const { alarm, audioEnabled, enableAudio, recompute, stopAndDispose } = useAlarm();
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const lastReminder = useRef<string | undefined>(undefined);

  // Realtime-Signal => vollständiger Server-Refresh (nie Payload als Order).
  const onRefresh = useCallback((reminderOrderId?: string) => {
    lastReminder.current = reminderOrderId;
    void reload();
  }, [reload]);

  const realtime = useMerchantRealtime({ profileId: profile?.id ?? null, onRefresh });
  const heartbeat = useHeartbeat(merchantId, terminalId.current, true);

  // Initial Load beim Betreten (Realtime macht zusätzlich einen Catch-up
  // nach SUBSCRIBED => eine Startorder wird nicht übersehen).
  useEffect(() => { void reload(); }, [reload]);

  // Alarm immer aus den AKTUELLEN Serverdaten ableiten.
  useEffect(() => {
    if (loadedOnce) {
      recompute(orders, lastReminder.current);
      lastReminder.current = undefined;
    }
  }, [orders, loadedOnce, recompute]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  // Aktion ausführen: Doppelklick-Schutz + immer Server-Refresh danach.
  const runAction = useCallback(async (orderId: string, fn: () => Promise<{ ok: boolean; code?: string }>) => {
    if (busyOrderId) return; // ein Vorgang zurzeit
    setBusyOrderId(orderId);
    try {
      const res = await fn();
      if (!res.ok) showToast(humanizeError(res.code));
    } finally {
      await reload(); // Serverzustand respektieren (auch bei Race/Auto-Reject)
      setBusyOrderId(null);
    }
  }, [busyOrderId, reload, showToast]);

  const onLogout = useCallback(async () => {
    stopAndDispose();
    await signOut();
  }, [stopAndDispose, signOut]);

  const g = groupOrders(orders);

  return (
    <div className="terminal">
      <TerminalStatusBar
        realtime={realtime}
        heartbeat={heartbeat}
        audioEnabled={audioEnabled}
        merchantName={merchantName ?? "Händler-Terminal"}
        onLogout={onLogout}
      />

      <AlarmBanner openCount={alarm.openCount} audioEnabled={audioEnabled} onEnableAudio={() => void enableAudio()} />

      {toast && <div className="toast" role="alert">{toast}</div>}
      {error && <div className="toast toast--warn" role="alert">Bestellungen konnten nicht geladen werden. Erneuter Versuch läuft…</div>}

      <main className="terminal__main">
        <Section title="Neu — Entscheidung erforderlich" count={g.decision.length} highlight>
          {g.decision.map((o) => (
            <OrderCard key={o.order_id} order={o} busy={busyOrderId === o.order_id}
              onAccept={(id, min) => runAction(id, () => acceptOrder(id, min))}
              onReject={(id, reason, comment) => runAction(id, () => rejectOrder(id, reason, comment))}
              onPreparing={(id) => runAction(id, () => markPreparing(id))}
              onReady={(id) => runAction(id, () => markReady(id))} />
          ))}
          {!g.decision.length && loadedOnce && <p className="empty">Keine neuen Bestellungen.</p>}
        </Section>

        <Section title="In Zubereitung" count={g.preparing.length}>
          {g.preparing.map((o) => (
            <OrderCard key={o.order_id} order={o} busy={busyOrderId === o.order_id}
              onAccept={() => {}} onReject={() => {}}
              onPreparing={(id) => runAction(id, () => markPreparing(id))}
              onReady={(id) => runAction(id, () => markReady(id))} />
          ))}
        </Section>

        <Section title="Bereit" count={g.ready.length}>
          {g.ready.map((o) => (
            <OrderCard key={o.order_id} order={o} busy={false}
              onAccept={() => {}} onReject={() => {}} onPreparing={() => {}} onReady={() => {}} />
          ))}
        </Section>

        {g.in_delivery.length > 0 && (
          <Section title="Unterwegs" count={g.in_delivery.length}>
            {g.in_delivery.map((o) => (
              <OrderCard key={o.order_id} order={o} busy={false}
                onAccept={() => {}} onReject={() => {}} onPreparing={() => {}} onReady={() => {}} />
            ))}
          </Section>
        )}
      </main>

      <SetupPanel audioEnabled={audioEnabled} onEnableAudio={() => void enableAudio()} />
    </div>
  );
}

function Section({ title, count, highlight, children }: { title: string; count: number; highlight?: boolean; children: React.ReactNode }) {
  return (
    <section className={`section ${highlight && count > 0 ? "section--highlight" : ""}`} aria-label={title}>
      <h2 className="section__title">{title} <span className="section__count">{count}</span></h2>
      <div className="section__grid">{children}</div>
    </section>
  );
}
